<i18n>
{
  "en": {
    "create": "Create",
    "edit": "Edit",
    "editMenuTitle": "Edit menu",
    "emptyFieldError": "Required field",
    "English": "English",
    "externalLink": "External link",
    "externalLinkTextEn": "English link's text",
    "externalLinkTextEs": "Spanish link's text",
    "fileFormatError": "This format is not valid",
    "fileSizeError": "Image is too big. Maximum size is 2 MB.",
    "image": "Select image from file",
    "imageHint": "Logo to show in the blackboard. Optional",
    "imageHintExisting": "Logo to show in the blackboard. Warning, the current one will be overridden!",
    "lengthFieldError": "The length of the field must be between 1 and 36",
    "newMenuTitle": "New menu",
    "previousFile": "Previous File",
    "selectDefaultSaleFormats": "Select default sale formats",
    "selectFirstLanguage": "Select menu main language",
    "selectMomentOfConsume": "Select moments of consume",
    "selectSaleZone": "Select sale zones",
    "selectSecondLanguage": "Select menu second language",
    "selectTemplate": "Select menu template",
    "selectThirdLanguage": "Select menu third language",
    "Spanish": "Spanish",
    "textError": "Mandatory field, 30 characters max",
    "title": "Title"
  },
  "es": {
    "create": "Crear",
    "edit": "Editar",
    "editMenuTitle": "Editar menú",
    "emptyFieldError": "Campo obligatorio",
    "English": "Inglés",
    "externalLink": "Enlace externo",
    "externalLinkTextEn": "Texto del enlace en inglés",
    "externalLinkTextEs": "Texto del enlace en español",
    "fileFormatError": "Este formato no es válido",
    "fileSizeError": "La imagen es demasiado grande. El tamaño máximo es 2 MB.",
    "footerText": "Texto en el pie del menú",
    "image": "Selecciona la imagen desde el explorador",
    "imageHint": "Logo a mostrar en la pizarra (Opcional)",
    "imageHintExisting": "Logo a mostrar en la pizarra. El actual será sobreescrito",
    "lengthFieldError": "La longitud del campo debe de estar entre 1 y 36 caracteres",
    "newMenuTitle": "Nuevo menú",
    "previousFile": "Imagen anterior",
    "Priority": "Prioridad",
    "selectDefaultSaleFormats": "Selecciona formatos de venta por defecto",
    "selectFirstLanguage": "Selecciona el idioma principal del menú",
    "selectMomentOfConsume": "Selecciona el momento de consumo",
    "selectSaleZone": "Selecciona las zonas",
    "selectSecondLanguage": "Selecciona el segundo idioma del menú",
    "selectTemplate": "Selecciona la plantilla del menú",
    "selectThirdLanguage": "Selecciona el tercer idioma del menú",
    "Spanish": "Español",
    "textError": "Campo obligatorio, máximo 30 caracteres",
    "title": "Título"
  }
}
</i18n>

<template>
  <v-dialog v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if=visible scrollable persistent class="px-7 py-4">
      <v-card-title class="pa-0 mb-2">
        {{ modalTitle }}
      </v-card-title>
      <v-sheet style="  background-color: #ffffff">
        <v-form v-model="valid" ref="form">
          <v-text-field v-model="title" :rules="titleRules" :label="$t('title')" class="required"/>
          <v-select v-model="defaultSaleFormats" :items="masterSaleFormatList"
                    :menu-props="{ maxHeight: '400' }" :label="$t('selectDefaultSaleFormats')"
                    item-value="guid" item-text="name" chips multiple class="required"/>
          <v-select :disabled="menu.is_managed" v-model="saleZones" :items="saleZoneList"
                    :menu-props="{ maxHeight: '400' }" :label="$t('selectSaleZone')"
                    :rules="listRules" item-value="guid" item-text="name" chips multiple
                    class="required"/>
          <v-select :disabled="menu.is_managed" v-model="momentsOfConsume"
                    :items="momentOfConsumeList"
                    :menu-props="{ maxHeight: '400' }" :label="$t('selectMomentOfConsume')"
                    :rules="listRules" item-value="guid" item-text="name" chips multiple
                    class="required"/>
          <v-select :disabled="templateSelectionDisabled" v-model="template" :items="templateList"
                    :menu-props="{ maxHeight: '400' }" :label="$t('selectTemplate')"
                    :rules="templateRules" item-value="guid" item-text="name" class="required"/>
          <v-select v-model="mainLanguage" :items="availableMainLanguages"
                    :label="$t('selectFirstLanguage')"
                    :rules="languageRules" :menu-props="{ maxHeight: '400' }"
                    item-text="translated_name"
                    item-value="guid"
                    :append-icon="mainLanguageDeleteIcon" @click:append="onDeleteMainLanguage"
                    class="required"/>
          <v-select v-model="secondLanguage" :items="availableSecondLanguages"
                    :label="$t('selectSecondLanguage')"
                    :menu-props="{ maxHeight: '400' }" item-text="translated_name" item-value="guid"
                    :append-icon="secondLanguageDeleteIcon" @click:append="onDeleteSecondLanguage"/>
          <v-select v-model="thirdLanguage" :items="availableThirdLanguages"
                    :label="$t('selectThirdLanguage')"
                    :menu-props="{ maxHeight: '400' }" item-text="translated_name" item-value="guid"
                    :append-icon="thirdLanguageDeleteIcon" @click:append="onDeleteThirdLanguage"/>

          <!-- Image selector -->
          <v-file-input v-if="showCustomImageField"
                        v-model="image" :rules="imageRules" :show-size="showImageSize"
                        accept="image/png, image/jpeg, image/bmp" prepend-inner-icon="mdi-camera"
                        prepend-icon="" persistent-hint :hint="imageHint" :label="$t('image')"
                        class="mb-6"/>

        </v-form>
        <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!valid"
                                :confirmText="$t('confirm')"/>
      </v-sheet>
    </v-card>
  </v-dialog>
</template>

<script>

import {
  imageFileType,
  maxSize,
  minMaxChars,
  notEmptyList,
  notEmptyObject,
  notEmptyString
} from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Components, MapGetters } from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { Menu } from '@dynameat/views/pointOfSale/models';
import DynameatLanguageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';

@Component
@Components({ DynameatModalButtons, DynameatLanguageFieldGenerator })
@MapGetters(['templateList', 'saleZoneList', 'momentOfConsumeList',
  'masterSaleFormatList', 'pointOfSaleLanguages', 'pointOfSaleLanguages'])
export default class MenuCreateEditModal extends Vue {
  @Prop({
    String,
    default: 'new',
    validator(type) {
      return type === 'new' || type === 'edit';
    }
  }) type;

  @Prop({
    Object,
    validator(menu) {
      return menu instanceof Menu;
    },
  }) menu;

  @Prop({ Boolean, default: false }) visible;

  valid = false;

  title = '';

  masterSaleFormat = '';

  saleZones = [];

  momentsOfConsume = [];

  template = null;

  defaultSaleFormats = [];

  mainLanguage = '';

  secondLanguage = '';

  thirdLanguage = '';

  image = null;

  get modalTitle() {
    return this.$t(`${this.type}MenuTitle`);
  }

  get templateSelectionDisabled() {
    return this.menu.is_managed || this.type === 'edit';
  }

  get titleRules() {
    return [notEmptyString(this.$t('emptyFieldError')), minMaxChars(this.$t('emptyFieldError'), 1, 64)];
  }

  get saleFormatRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get listRules() {
    return [notEmptyList(this.$t('emptyFieldError'))];
  }

  get templateRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get languageRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get imageRules() {
    return [maxSize(this.$t('fileSizeError'), 2000000), imageFileType(this.$t('fileFormatError'))];
  }

  get imageHint() {
    if (this.image) {
      return this.$t('imageHintExisting');
    }
    return this.$t('imageHint');
  }

  get showImageSize() {
    return this.image instanceof File && this.image.size > 0;
  }

  get showCustomImageField() {
    const templateObject = this.templateList.find((el) => el.guid === this.template);
    if (templateObject) {
      return !templateObject?.isOperationDisallowed('MENU_EDITION', 'USE_CUSTOM_LOGO');
    }
    return false;
  }

  get showBrandingImageField() {
    const templateObject = this.templateList.find((el) => el.guid === this.template);
    if (templateObject) {
      return !templateObject?.isOperationDisallowed('MENU_EDITION', 'USE_BRANDING_IMAGE');
    }
    return false;
  }

  get linkText() {
    if (this.externalLink !== null && this.externalLink !== '') {
      return [notEmptyString(this.$t('emptyFieldError')), minMaxChars(this.$t('lengthFieldError'), 1, 36)];
    }
    return [true, minMaxChars(this.$t('lengthFieldError'), 0, 36)];
  }

  getAvailableLanguages(primaryLanguage, excludedLanguages) {
    const availableLanguages = this.pointOfSaleLanguages.filter(
      (language) => !excludedLanguages.includes(language.guid)
    );

    return availableLanguages.map((lang) => ({
      ...lang,
      translated_name: this.$t(lang.name),
    }));
  }

  get availableMainLanguages() {
    return this.getAvailableLanguages(
      this.mainLanguage,
      [this.secondLanguage, this.thirdLanguage]
    );
  }

  get availableSecondLanguages() {
    return this.getAvailableLanguages(
      this.secondLanguage,
      [this.mainLanguage, this.thirdLanguage]
    );
  }

  get availableThirdLanguages() {
    return this.getAvailableLanguages(
      this.thirdLanguage,
      [this.mainLanguage, this.secondLanguage]
    );
  }

  get mainLanguageDeleteIcon() {
    return !this.mainLanguage ? undefined : 'mdi-trash-can';
  }

  get secondLanguageDeleteIcon() {
    return !this.secondLanguage ? undefined : 'mdi-trash-can';
  }

  get thirdLanguageDeleteIcon() {
    return !this.thirdLanguage ? undefined : 'mdi-trash-can';
  }

  @Watch('menu', { immediate: true })
  onMenuChanged(updatedMenu) {
    if (updatedMenu) {
      this.clearOwnData();
      this.title = updatedMenu.title;
      this.defaultSaleFormats = updatedMenu.default_sale_formats;
      this.saleZones = updatedMenu.sale_zones;
      this.momentsOfConsume = updatedMenu.moments_of_consume;
      this.template = updatedMenu.template;
      this.priority = updatedMenu.priority;
      this.externalLink = updatedMenu.external_link;
      this.externalLinkTextEs = updatedMenu.external_link_text_es;
      this.externalLinkTextEn = updatedMenu.external_link_text_en;
      this.mainLanguage = updatedMenu.mainLanguage;
      this.secondLanguage = updatedMenu.secondLanguage;
      this.thirdLanguage = updatedMenu.thirdLanguage;

      this.image = updatedMenu.custom_logo ? new File([], this.$t('previousFile')) : null;
    }
  }

  clearOwnData() {
    this.valid = false;
    this.title = '';
    this.masterSaleFormat = '';
    this.saleZones = [];
    this.momentsOfConsume = [];
    this.template = null;
    this.defaultSaleFormats = [];
    this.mainLanguage = '';
    this.secondLanguage = '';
    this.thirdLanguage = '';
    this.image = null;
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  onConfirm() {
    if (this.$refs.form.validate()) {
      this.menu.title = this.title;
      this.menu.default_sale_formats = this.defaultSaleFormats;
      this.menu.sale_zones = this.saleZones;
      this.menu.moments_of_consume = this.momentsOfConsume;
      this.menu.template = this.template;
      this.menu.mainLanguage = this.mainLanguage;
      this.menu.secondLanguage = this.secondLanguage;
      this.menu.thirdLanguage = this.thirdLanguage;
      this.menu.custom_logo = this.image;
      this.$emit('confirm', this.menu);
    }
  }

  onDeleteMainLanguage() {
    this.mainLanguage = '';
  }

  onDeleteSecondLanguage() {
    this.secondLanguage = '';
  }

  onDeleteThirdLanguage() {
    this.thirdLanguage = '';
  }
}
</script>
<style scoped>
.required label::after {
  content: " *";
  color: red;
}
</style>
